import type { BoardTemplate } from '@molt/shared';
import { Pencil, Save, Trash2, X } from 'lucide-react';
import { useEffect, useId, useState, type ReactElement } from 'react';

import { Button, Notice, TextField, profileName } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface TemplateDescriptionSectionProps {
  readonly template: BoardTemplate | null;
  readonly canEdit: boolean;
  readonly busy?: boolean | undefined;
  readonly error?: string | undefined;
  readonly onSave: (description: string | null) => void;
}

export function TemplateDescriptionSection({
  template,
  canEdit,
  busy,
  error,
  onSave,
}: TemplateDescriptionSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const headingId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(template?.templateDescription ?? '');

  const description = template?.templateDescription?.trim() ?? '';
  const hasDescription = description.length > 0;
  const creator = template?.creator === null ? null : template?.creator;
  const creatorLabel =
    creator === undefined
      ? t('boardTemplates.description.creatorUnavailable')
      : creator === null
        ? t('boardTemplates.description.creatorUnavailable')
        : t('boardTemplates.description.createdBy', { name: profileName(creator) });
  const controlsDisabled = busy === true || template === null;
  const busyProps = busy === undefined ? {} : { busy };

  useEffect(() => {
    if (editing) return;
    setDraft(template?.templateDescription ?? '');
  }, [editing, template?.templateDescription]);

  function saveDraft() {
    if (controlsDisabled) return;
    const trimmed = draft.trim();
    onSave(trimmed.length === 0 ? null : trimmed);
    setEditing(false);
  }

  function clearDescription() {
    if (controlsDisabled) return;
    setDraft('');
    onSave(null);
    setEditing(false);
  }

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={headingId} className="text-sm font-semibold text-ink">
            {t('boardTemplates.description.heading')}
          </h3>
          <p className="mt-1 text-xs text-ink-muted">{creatorLabel}</p>
        </div>

        {canEdit && !editing ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {hasDescription ? (
              <Button
                type="button"
                size="sm"
                variant="quiet"
                iconStart={<Trash2 aria-hidden="true" className="size-4" />}
                disabled={controlsDisabled}
                onClick={clearDescription}
              >
                {t('boardTemplates.description.clear')}
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              iconStart={<Pencil aria-hidden="true" className="size-4" />}
              disabled={controlsDisabled}
              onClick={() => setEditing(true)}
            >
              {t('boardTemplates.description.edit')}
            </Button>
          </div>
        ) : null}
      </div>

      {error === undefined ? null : (
        <Notice
          tone="bad"
          title={t('boardTemplates.description.errorTitle')}
          body={error}
        />
      )}

      {editing ? (
        <div className="flex flex-col gap-3">
          <TextField
            label={t('boardTemplates.description.fieldLabel')}
            multiline
            value={draft}
            disabled={controlsDisabled}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="primary"
              iconStart={<Save aria-hidden="true" className="size-4" />}
              disabled={controlsDisabled}
              busyLabel={t('boardTemplates.description.busy')}
              onClick={saveDraft}
              {...busyProps}
            >
              {t('boardTemplates.description.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              iconStart={<Trash2 aria-hidden="true" className="size-4" />}
              disabled={controlsDisabled}
              onClick={clearDescription}
            >
              {t('boardTemplates.description.clear')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="quiet"
              iconStart={<X aria-hidden="true" className="size-4" />}
              disabled={busy === true}
              onClick={() => {
                setDraft(template?.templateDescription ?? '');
                setEditing(false);
              }}
            >
              {t('boardTemplates.description.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap rounded-card bg-surface-raised px-3 py-2 text-sm text-ink-muted">
          {hasDescription ? description : t('boardTemplates.description.empty')}
        </p>
      )}
    </section>
  );
}
